import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bot,
  Mail,
  Inbox,
  ScrollText,
  MessageSquareMore,
  MessageSquare,
  FolderOpen,
  ListChecks,
  Sparkles,
  Gauge,
  CheckSquare,
} from 'lucide-react';
import { useCan, visibleTabs, pickActiveTab, NoAccess } from '../../lib/rbac';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import { loadMeta, refreshMeta, type Tab } from './shared';
import EmailsTab from './EmailsTab';
import MailboxesTab from './MailboxesTab';
import DirectivesTab from './DirectivesTab';
import InboxTab from './InboxTab';
import ChatTab from './ChatTab';
import CasesTab from './CasesTab';
import ActionsTab from './ActionsTab';
import CapabilitiesTab from './CapabilitiesTab';
import BrainTab from './BrainTab';
import DecisionsTab from './DecisionsTab';

const UNREAD_REFRESH_MS = 60_000;

export default function AssistantPage() {
  const { t } = useTranslation();
  const can = useCan();
  const [tab, setTab] = useState<Tab>('chat');
  const sellerId = useUIStore((s) => s.sellerId);
  const [unread, setUnread] = useState<number | null>(null);
  /** Avisos nuevos del asistente en las conversaciones del usuario (etiqueta "Conversación"). */
  const [alertsNew, setAlertsNew] = useState(0);
  const unreadSeq = useRef(0);
  const [openCases, setOpenCases] = useState(0);
  // Caso a abrir en la pestaña Casos ("Ver caso" desde un correo o el registro).
  const [focusCase, setFocusCase] = useState<{ id: number; nonce: number } | null>(null);

  // Contador de avisos sin leer de la bandeja y de avisos nuevos del asistente en las
  // conversaciones (para las etiquetas de las pestañas). El servidor viejo no manda alerts_new.
  const loadUnread = useCallback(async () => {
    const seq = ++unreadSeq.current;
    try {
      const res = await api.get<{ inbox_unread: number; alerts_new?: number }>(
        `/assistant/summary?seller_id=${sellerId}`
      );
      if (seq !== unreadSeq.current) return;
      setUnread(res.success && res.data ? Number(res.data.inbox_unread) || 0 : null);
      setAlertsNew(res.success && res.data ? Number(res.data.alerts_new) || 0 : 0);
    } catch {
      if (seq === unreadSeq.current) {
        setUnread(null);
        setAlertsNew(0);
      }
    }
  }, [sellerId]);

  // Propuestas del cerebro que esperan decisión (para la etiqueta de la pestaña Decisiones).
  const canDecisions = can('assistant.tab.decisions');
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const pendingSeq = useRef(0);
  const loadPendingCount = useCallback(async () => {
    if (!canDecisions) return;
    const seq = ++pendingSeq.current;
    try {
      const res = await api.get<{ counts: { total: number } }>('/assistant/decisions?limit=1');
      if (seq !== pendingSeq.current) return;
      setPendingCount(res.success && res.data ? Number(res.data.counts?.total) || 0 : null);
    } catch {
      if (seq === pendingSeq.current) setPendingCount(null);
    }
  }, [canDecisions]);

  useEffect(() => {
    if (!canDecisions) return;
    void loadPendingCount();
    const id = setInterval(() => void loadPendingCount(), UNREAD_REFRESH_MS);
    return () => clearInterval(id);
  }, [canDecisions, loadPendingCount]);

  // Reglas que el cerebro propone y esperan decisión (para la etiqueta de la pestaña Directivas).
  // El servidor exige además ser Administrador: no pedir el contador si no lo es.
  const isOwner = useAuthStore((s) => !!s.user?.is_admin);
  const canDirectives = can('assistant.tab.directives') && isOwner;
  const [rulesCount, setRulesCount] = useState<number | null>(null);
  const rulesSeq = useRef(0);
  const loadRulesCount = useCallback(async () => {
    if (!canDirectives) return;
    const seq = ++rulesSeq.current;
    try {
      const res = await api.get<{ counts: { candidata: number } }>('/assistant/rules?limit=1');
      if (seq !== rulesSeq.current) return;
      setRulesCount(res.success && res.data ? Number(res.data.counts?.candidata) || 0 : null);
    } catch {
      if (seq === rulesSeq.current) setRulesCount(null);
    }
  }, [canDirectives]);

  useEffect(() => {
    if (!canDirectives) return;
    void loadRulesCount();
    const id = setInterval(() => void loadRulesCount(), UNREAD_REFRESH_MS);
    return () => clearInterval(id);
  }, [canDirectives, loadRulesCount]);

  useEffect(() => {
    void loadUnread();
    const id = setInterval(() => void loadUnread(), UNREAD_REFRESH_MS);
    return () => clearInterval(id);
  }, [loadUnread]);

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    {
      key: 'chat',
      label: alertsNew ? `Conversación (${alertsNew})` : 'Conversación',
      icon: <MessageSquare size={16} />,
    },
    { key: 'emails', label: 'Correos', icon: <Mail size={16} /> },
    { key: 'mailboxes', label: 'Casillas', icon: <Inbox size={16} /> },
    {
      key: 'directives',
      label: rulesCount ? `Directivas y cuaderno (${rulesCount})` : 'Directivas y cuaderno',
      icon: <ScrollText size={16} />,
    },
    {
      key: 'inbox',
      label: unread ? `Bandeja (${unread})` : 'Bandeja',
      icon: <MessageSquareMore size={16} />,
    },
    {
      key: 'cases',
      label: openCases > 0 ? `Casos (${openCases})` : 'Casos',
      icon: <FolderOpen size={16} />,
    },
    { key: 'actions', label: 'Registro', icon: <ListChecks size={16} /> },
    { key: 'capabilities', label: 'Capacidades', icon: <Sparkles size={16} /> },
    {
      key: 'decisions',
      label: pendingCount ? `Decisiones (${pendingCount})` : 'Decisiones',
      icon: <CheckSquare size={16} />,
    },
    { key: 'brain', label: 'Medición', icon: <Gauge size={16} /> },
  ];

  const allowedTabs = visibleTabs('assistant', tabs, can);
  const active = pickActiveTab('assistant', tabs, tab, can) as Tab | null;
  useEffect(() => {
    if (active && active !== tab) setTab(active);
  }, [active, tab]);

  // Etiquetas de tipos y estados para las pestañas (se piden una sola vez).
  useEffect(() => {
    void loadMeta().then((m) => setOpenCases(Number(m?.open_cases) || 0));
  }, []);

  // Tras resolver / descartar / reintentar un caso: refrescar el contador de casos abiertos.
  const reloadOpenCases = useCallback(() => {
    void refreshMeta().then((m) => {
      if (m) setOpenCases(Number(m.open_cases) || 0);
    });
  }, []);

  const canCases = can('assistant.tab.cases');
  const openCase = useCallback((id: number) => {
    setFocusCase((f) => ({ id, nonce: (f?.nonce ?? 0) + 1 }));
    setTab('cases');
  }, []);
  const onOpenCase = canCases ? openCase : undefined;

  if (active === null) return <NoAccess />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Bot size={24} className="text-[var(--color-primary)]" />
        <h1 className="text-xl font-bold text-[var(--color-text)]">{t('nav.assistant')}</h1>
      </div>

      <div className="flex flex-wrap gap-1 bg-[var(--color-bg-secondary)] p-1 rounded-lg w-fit">
        {allowedTabs.map(({ key, label, icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              active === key
                ? 'bg-[var(--color-primary)] text-white'
                : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text)] hover:bg-[var(--color-bg-tertiary)]'
            }`}
          >
            {icon}
            {label}
          </button>
        ))}
      </div>

      {active === 'chat' &&
        (can('assistant.chat') ? (
          <ChatTab />
        ) : (
          <div className="text-sm text-[var(--color-text-muted)]">
            No tenés permiso para conversar con el asistente.
          </div>
        ))}
      {active === 'emails' && <EmailsTab onOpenCase={onOpenCase} onChanged={reloadOpenCases} />}
      {active === 'mailboxes' && <MailboxesTab />}
      {active === 'directives' && <DirectivesTab onRulesChanged={() => void loadRulesCount()} />}
      {active === 'inbox' && <InboxTab onChanged={() => void loadUnread()} />}
      {active === 'cases' && (
        <CasesTab
          focusCaseId={focusCase?.id ?? null}
          focusNonce={focusCase?.nonce}
          onFocusConsumed={() => setFocusCase(null)}
          onChanged={reloadOpenCases}
        />
      )}
      {active === 'actions' && <ActionsTab onOpenCase={onOpenCase} onChanged={reloadOpenCases} />}
      {active === 'capabilities' && <CapabilitiesTab />}
      {active === 'decisions' && <DecisionsTab onChanged={() => void loadPendingCount()} />}
      {active === 'brain' && <BrainTab />}
    </div>
  );
}
